import $ from 'jquery';
import { parseDynamicContent } from 'jquery.validate';
import validateGroup from 'validate-group';

export default function(props = {}) {
    const itemsKey = props.itemsKey ?? (isObject(props.data) ? Object.keys(props.data).find(k => Array.isArray(props.data[k])) : null) ?? 'items';
    const newItemKey = props.newItemKey ?? (isObject(props.data) ? Object.keys(props.data).find(k => !Array.isArray(props.data[k])) : null) ?? 'newItem'
    const editItemKey = props.editItemKey ?? 'editItem';

    const defaultData = {
        [itemsKey]: [],
        [newItemKey]: {},
        [editItemKey]: null,
        filter: {}
    };

    let data;

    if (props.data === undefined)
        data = defaultData;
    else if (Array.isArray(props.data))
        data = { ...defaultData, ...{ [itemsKey]: props.data } };
    else
        data = { ...defaultData, ...props.data };

    const originalNewItem = copy(data[newItemKey]);

    return {
        itemsKey,
        newItemKey,
        editItemKey,
        ...data,
        originalNewItem,
        enablePaging: props.enablePaging ?? false,
        page: props.page ?? 1,
        enableSorting: props.enableSorting ?? false,
        sortDirection: props.sortDirection ?? 'Ascending',
        // This should be used with dynamic lists. However it should be enabled with caution as it may cause problems with nested list components (since the inner list will bubble up and trigger the same event as if it was fired from the parent).
        eventBubbles: props.eventBubbles ?? false,
        sort(name, direction = null) {
            // Set the sort by and direction.
            this.sortDirection = direction ?? (this.sortBy == name && this.sortDirection == 'Ascending' ? 'Descending' : 'Ascending');
            this.sortBy = name;
        },
        reset(item, e = event) {
            // Restore the item back to it's original state.
            item = restore(item, this.originalNewItem);

            // Get the container to trigger the reset against.
            const container = e.target.closest('[data-val-group]') || e.target.closest('form') || this.$refs.root;

            // Handling this event allows you to manually reset a field element, e.g. form files.
            container.dispatchEvent(new Event('reset', { bubbles: true }));
        },
        edit(item, e = event) {
            // Trigger the editing event.
            const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('list:editing', { bubbles: this.eventBubbles, cancelable: true, detail: { list: this, item, event: e } }));

            // Make sure the default has not been prevented.
            if (!cancelled) {
                // Copy the original state of the edit item (before it is edited).
                this.originalEditItem = copy(item);

                // Trigger the edited event.
                this.$refs.root.dispatchEvent(new CustomEvent('list:edited', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } }));

                // Set the item we are editing.
                this[this.editItemKey] = item;

                // Trigger the edited view updated event. Wrapping in $nextTick will make sure it has switched to the edit template.
                this.$nextTick(() => this.$refs.root.dispatchEvent(new CustomEvent('list:edited:viewUpdated', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } })));
            }
        },
        cancel(item, e = event) {
            // Trigger the cancelling event.
            const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('list:cancelling', { bubbles: this.eventBubbles, cancelable: true, detail: { list: this, item, event: e } }));

            // Make sure the default has not been prevented.
            if (!cancelled) {
                // Restore the item to it's original state.
                item = restore(item, this.originalEditItem);

                // Trigger the cancelled event.
                this.$refs.root.dispatchEvent(new CustomEvent('list:cancelled', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } }));

                // Set the item we are editing to none.
                this[this.editItemKey] = null;

                // Trigger the cancelled view updated event. Wrapping in $nextTick will make sure it has switched to the item template.
                this.$nextTick(() => this.$refs.root.dispatchEvent(new CustomEvent('list:cancelled:viewUpdated', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } })));
            }
        }
    };
}

export function copy(item) {
    const newItem = {};

    for (const key in item) {
        const value = item[key];

        if (Array.isArray(value))
            newItem[key] = [...value].map(v => isObject(v) ? copy(v) : v);
        else if (isObject(value))
            newItem[key] = copy(value);
        else
            newItem[key] = value;
    }

    return newItem;
}

export function isObject(obj) {
    return typeof obj == 'object' && obj != null;
}

export function isValid(target) {
    // Try to get the parent form.
    const form = target.closest('form');

    // If no form is found then we can't validate.
    if (form == null)
        return true;

    // This fixes an issue when a Vue.js list component is originally empty, then the unobtrusive validation library won't find the edit template fields when it parses the form.
    parseDynamicContent(form);

    // Try to get the parent validation group.
    const validationGroup = target.closest('[data-val-group]');

    if (validationGroup)
        return validateGroup(form, validationGroup.dataset.valGroup);
    else
        return $(form).valid();
}

export function restore(item, originalItem, defaultValue = '') {
    for (const key in item) {
        const originalValue = originalItem[key];

        item[key] = originalValue !== undefined ? originalValue : defaultValue;
    }

    return item;
}